import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { pollDonationStatus } from "@/lib/api";

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "", amount: 0, currency: "gbp" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing" });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      attempts.current += 1;
      try {
        const data = await pollDonationStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", ...data });
          return;
        }
        if (data.status === "expired" || attempts.current > 8) {
          setState({ status: data.status === "expired" ? "expired" : "timeout", ...data });
          return;
        }
        setState({ status: "pending", ...data });
        setTimeout(tick, 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error" });
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar showAnnouncement={false} />
      <section className="pt-32 pb-24 max-w-2xl mx-auto px-6 lg:px-10 text-center" data-testid="donation-success-page">
        {state.status === "checking" || state.status === "pending" ? (
          <>
            <Loader2 className="mx-auto text-blue-600 animate-spin" size={56} strokeWidth={1.5} />
            <h1 className="mt-8 font-display text-4xl md:text-5xl font-medium tracking-tighter text-slate-900">
              Confirming your donation...
            </h1>
            <p className="mt-5 text-lg text-slate-600">Hold on a moment while we verify your payment.</p>
          </>
        ) : state.status === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto text-blue-600" size={56} strokeWidth={1.5} />
            <h1 className="mt-8 font-display text-4xl md:text-5xl font-medium tracking-tighter text-slate-900 text-balance">
              Thank you. <span className="font-serif-accent text-blue-600">Truly.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Your donation of <strong className="text-slate-900">£{Number(state.amount).toFixed(2)}</strong> goes straight into the next build. We&apos;ll send you an update when the site we sponsored with your gift goes live.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-outline-light" data-testid="success-back-home">Back to home</Link>
              <Link to="/donate" className="btn-primary" data-testid="success-donate-again">Donate again <ArrowRight size={18} /></Link>
            </div>
          </>
        ) : (
          <>
            <XCircle className="mx-auto text-slate-400" size={56} strokeWidth={1.5} />
            <h1 className="mt-8 font-display text-4xl md:text-5xl font-medium tracking-tighter text-slate-900">
              We couldn&apos;t confirm your payment.
            </h1>
            <p className="mt-5 text-lg text-slate-600">{state.status === "expired" ? "The session expired before payment completed." : "If you were charged, contact us at foundation@dhwebsiteservices.co.uk and we'll resolve it within 24 hours."}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/donate" className="btn-primary" data-testid="success-retry">Try again</Link>
              <Link to="/" className="btn-outline-light">Back to home</Link>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
